/**
 * NEXT-TRACKING-V2-CUTOVER — wire types + view-model mapper for the public tracking page
 * `GET /v2/tracking/{trackingNumber}?carrier=<code>`.
 *
 * The v2 wire contract (read out of the MERGED C# on ShipvistaAPI origin/dev 2026-07-24 — as-built
 * beats the spec prose per GUARDRAILS §2; `PublicTrackingEndpoints.cs` + `PublicTracking.cs` +
 * `PublicTrackingLookup.cs` + `migrations/0730_public_tracking_lookup.up.sql`):
 *   - Route is UNAUTHENTICATED by design (no `Authorization`, no tenant id). `carrier` is a REQUIRED
 *     query param; missing/blank → 400 `carrier-required` (problem+json).
 *   - 200 body is `PublicTrackingResult`, camelCase (System.Text.Json web defaults), enums as strings.
 *     `currentStatusCode` is a NORMALIZED code (NOT display text) — `UNKNOWN` is a real emitted value.
 *     `events[]` is ordered occurred_at DESC, capped at 50 by the SQL function.
 *   - `branding` is ALWAYS returned via a three-rung fallback chain; only the top rung (tenant
 *     tracking-branding enabled AND entitled AND not over-quota) sets `isBranded: true`. The page
 *     renders branded chrome ONLY when `isBranded === true`; lower rungs are the ShipVista default.
 *   - `branding.logoUrl` is PRESIGNED (~1 h expiry) — never cache or bake it; fetch it with the
 *     response. `events[].description` / `.location` and `branding.accentColorHex` / `.supportUrl`
 *     are nullable.
 *   - Errors are RFC 7807 `application/problem+json` with REAL HTTP statuses. The v1
 *     `HTTP 200 + status:false` trap is gone. Known codes: 400 `carrier-required`,
 *     404 `tracking-not-found`. 429 `rate-limited` is spec'd but NOT implemented (endpoint remarks:
 *     "rate-limiting is a fast-follow") — the service maps a 429 defensively but it is unverified.
 *
 * The response carries NO tenant id by construction (the lookup strips it) — do not look for one.
 *
 * What v2 does NOT provide (do NOT invent these fields): estimated/expected delivery date, service
 * name, origin/destination address, recipient name, weight/dimensions, signatory, a human-readable
 * status label, a `message` string, any total or count. If a page design wants one, it is a NEW API
 * ticket — see this ticket's Findings.
 */

// ── v2 wire types (the 200 body of GET /v2/tracking/{trackingNumber}) ─────────

/** v2 `PublicTrackingEvent` wire shape (Tracking.Contracts) — one row of the timeline. */
export interface V2PublicTrackingEvent {
  eventCode: string;
  description: string | null;
  location: string | null;
  occurredAt: string; // ISO-8601 offset timestamp
}

/**
 * v2 `PublicTrackingBranding` wire shape (Tracking.Contracts — mirrors Platform.TenantBranding but
 * stays BCL-only). `accentColorHex` / `supportUrl` are nullable; `isBranded` is the gate.
 */
export interface V2PublicTrackingBranding {
  displayName: string;
  logoUrl: string;
  supportEmail: string;
  primaryColorHex: string;
  accentColorHex?: string | null;
  supportUrl?: string | null;
  /** TRUE only on the top fallback rung (tenant tracking-branding enabled + entitled + not over-quota). */
  isBranded: boolean;
}

/** v2 `PublicTrackingResult` — the 200 body. Carries NO tenant id by construction. */
export interface V2PublicTrackingResult {
  carrierCode: string;
  trackingNumber: string;
  currentStatusCode: string;
  isException: boolean;
  events: V2PublicTrackingEvent[];
  branding: V2PublicTrackingBranding;
}

// ── view models ──────────────────────────────────────────────────────────────

/** A timeline event shaped for display — nullable fields collapse to the "—" fallback. */
export interface TrackingTimelineEvent {
  /** ISO string preserved for date/time formatting at the call site. */
  occurredAt: string;
  description: string;
  location: string;
  eventCode: string;
}

/** Validated, page-safe branding chrome. Colours are absent when they fail hex validation. */
export interface TrackingBrandingChrome {
  isBranded: boolean;
  displayName: string;
  logoUrl: string;
  supportEmail: string;
  primaryColorHex: string | null;
  accentColorHex: string | null;
  supportUrl: string | null;
}

/** The full public-tracking view model the page consumes. */
export interface PublicTrackingViewModel {
  carrierCode: string;
  trackingNumber: string;
  /** Display label resolved from `currentStatusCode` via STATUS_LABELS (fallback arm included). */
  currentStatusLabel: string;
  currentStatusCode: string;
  isException: boolean;
  events: TrackingTimelineEvent[];
  branding: TrackingBrandingChrome;
}

// ── UI-carrier-value → v2 snake_case code map ────────────────────────────────

/**
 * The carrier code VOCABULARY changed in v2. v2 uses `CarrierCodes.ToCode` snake_case
 * (`canada_post` · `ups` · `purolator` · `canpar` · `usps` · `intelcom` · `day_ross` · `tforce`).
 *
 * This repo's `listOfCarriers` (src/utils/constants.ts) currently sends PascalCase / SCREAMING
 * values (`CanadaPost`, `ups`, `usps`, `CANPAR`, `PUROLATOR`, `SHIPVISTACONNECT`, `DAYANDROSS`).
 * `CanadaPost` and `CANPAR` are BOTH WRONG for v2. And the failure is SILENT: the endpoint does NOT
 * validate the code — it passes the raw string to the `public_tracking_lookup` SQL function, whose
 * match is an equality predicate on `carrier_code`. So a wrong-cased code matches no row and returns
 * 404 `tracking-not-found`, indistinguishable from "no such parcel" (verified vs `0730_*.up.sql` +
 * `PublicTrackingEndpoints.cs`). Map the UI values to v2 codes EXPLICITLY here.
 *
 * `SHIPVISTACONNECT` has no v2 equivalent (`CarrierCodes` is a closed 8-member enum with no
 * `ShipVistaConnect`); it maps to `""` so the page flags an unsupported selection rather than
 * silently 404 — the page treats `""` as a client-side guard (no request fired) and surfaces an
 * inline error on the carrier selector.
 */
const UI_TO_V2_CARRIER_CODE: Record<string, string> = {
  CanadaPost: "canada_post",
  ups: "ups",
  usps: "usps",
  CANPAR: "canpar",
  PUROLATOR: "purolator",
  DAYANDROSS: "day_ross",
  // No v2 equivalent — flagged client-side so the page never fires a guaranteed-404 request.
  SHIPVISTACONNECT: "",
};

/** The closed set of v2 carrier codes (`CarrierCodes.ToCode`). Used to pass already-correct values
 *  through unchanged (a deep link may carry a v2 code, not a UI value). */
const V2_CARRIER_CODES = new Set([
  "canada_post",
  "ups",
  "purolator",
  "canpar",
  "usps",
  "intelcom",
  "day_ross",
  "tforce",
]);

/**
 * Map a UI carrier value (a `listOfCarriers[].value`) to its v2 snake_case code. Returns `""` when
 * the selection has no v2 equivalent (the caller guards before firing the request). Unknown values
 * (a future carrier added to `listOfCarriers` without a map entry) also return `""` — fail closed
 * rather than firing a silent 404.
 *
 * A value that is ALREADY a valid v2 code (e.g. a hand-crafted deep link `?carrier=canada_post`)
 * passes through unchanged so the page does not bounce a correct link.
 */
export function toV2CarrierCode(uiCarrier: string | undefined | null): string {
  if (!uiCarrier) return "";
  if (V2_CARRIER_CODES.has(uiCarrier)) return uiCarrier;
  return UI_TO_V2_CARRIER_CODE[uiCarrier] ?? "";
}

// ── currentStatusCode → display label map (with fallback arm) ────────────────

/**
 * `currentStatusCode` is a NORMALIZED code, NOT display text. The page owns the code → label map.
 * `UNKNOWN` is a real emitted value, and unmapped codes are expected (per-event carrier codes flow
 * through `event_code`), so EVERY lookup needs the fallback arm (this codebase's standing rule).
 *
 * The codes below are the common carrier-neutral statuses the public page can show. v2 does not
 * publish the closed set of `currentStatusCode` values (the field is the latest event's raw
 * `event_code`, which is carrier-specific), so this map is necessarily partial — the fallback arm
 * is load-bearing, not decorative. A unmapped code renders as a readable "In progress" rather than
 * the raw code or empty space.
 */
const STATUS_LABELS: Record<string, string> = {
  DELIVERED: "Delivered",
  OUT_FOR_DELIVERY: "Out for delivery",
  IN_TRANSIT: "In transit",
  ACCEPTED: "Accepted",
  PICKED_UP: "Picked up",
  EXCEPTION: "Exception",
  UNKNOWN: "Unknown",
};

const STATUS_FALLBACK = "In progress";

function statusLabel(code: string | undefined | null): string {
  if (!code) return STATUS_FALLBACK;
  return STATUS_LABELS[code] ?? STATUS_FALLBACK;
}

// ── hex validation (branding colours arrive from an external source) ─────────

/**
 * Branding `primaryColorHex` / `accentColorHex` arrive as hex strings from an EXTERNAL tenant
 * payload. Validate the shape before use; never interpolate an unvalidated string into a stylesheet
 * (a malformed value could break the CSS custom property or, worse, escape it). Accepts `#RGB`,
 * `#RRGGBB`, and `#RRGGBBAA` (case-insensitive). Returns `null` for anything else — the caller then
 * omits the custom property and the default chrome colour shows through.
 */
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export function safeHex(value: string | null | undefined): string | null {
  return typeof value === "string" && HEX_RE.test(value.trim())
    ? value.trim()
    : null;
}

// ── response mapping ─────────────────────────────────────────────────────────

const DASH = "—";

/**
 * Map the v2 `PublicTrackingResult` into the page's view model. Nullable wire fields collapse to
 * the `"—"` fallback so `null` / `undefined` is never rendered into the DOM. Colours are validated;
 * invalid hex becomes `null` (omitted from the inline style, default chrome shows through).
 */
export function mapPublicTracking(
  result: V2PublicTrackingResult
): PublicTrackingViewModel {
  const events: TrackingTimelineEvent[] = (result.events ?? []).map((e) => ({
    eventCode: e.eventCode ?? "",
    occurredAt: e.occurredAt ?? "",
    description: e.description?.trim() ? e.description.trim() : DASH,
    location: e.location?.trim() ? e.location.trim() : DASH,
  }));

  const b = result.branding;
  const branding: TrackingBrandingChrome = {
    isBranded: b?.isBranded === true,
    displayName: b?.displayName?.trim() ? b.displayName.trim() : "ShipVista",
    logoUrl: b?.logoUrl ?? "",
    supportEmail: b?.supportEmail ?? "",
    primaryColorHex: safeHex(b?.primaryColorHex),
    accentColorHex: safeHex(b?.accentColorHex),
    supportUrl: b?.supportUrl ?? null,
  };

  return {
    carrierCode: result.carrierCode ?? "",
    trackingNumber: result.trackingNumber ?? "",
    currentStatusCode: result.currentStatusCode ?? "",
    currentStatusLabel: statusLabel(result.currentStatusCode),
    isException: result.isException === true,
    events,
    branding,
  };
}
